import ClassNames from "classnames";
import { FunctionComponent, useRef, useState } from "react";
import globalTranslations from "../../i18n/index.js";
import { getActiveSite } from "../../selectors/sites.js";
import { useAppSelector } from "../../store/hooks.js";
import moduleBaseStyles from "../../stylesheets/components/module-base.module.css";
import styles from "../../stylesheets/components/peer-tv-module.module.css";
import { BaseModuleProps, PeerTvModuleSettings } from "../../types/index.js";
import { css } from "../../utils/emotion.js";
import { useDidMountEffect } from "../../utils/hooks.js";
import {
  getActiveColorScheme,
  getSupportedLanguage,
  getTranslations,
} from "../../utils/utils.js";
import Icon from "../Icon.js";
import LazyloadWrapper from "../LazyloadWrapper.js";
import ModuleHeadings from "../ModuleHeadings.js";
import ModuleWithHeadings from "../ModuleWithHeadings.js";

type Props = BaseModuleProps<PeerTvModuleSettings>;

const PeerTvModule: FunctionComponent<Props> = ({
  translatedModule: {
    id: moduleId,
    translation: {
      languageId,
      settings: { title, subtitle, scriptUrl, caption, websiteUrl },
    },
    settings: { textAlign, size },
  },
  translatedModule,
  isFirstOnPage,
}) => {
  const scheme = useAppSelector((state) =>
    getActiveColorScheme(
      state.colorSchemes,
      getActiveSite(state.sites),
      translatedModule,
    ),
  );
  const [isLazyloaded, setIsLazyloaded] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const i18n = getTranslations(languageId, globalTranslations);
  const supportedLanguage = getSupportedLanguage(languageId, [
    "de",
    "it",
    "en",
  ]);
  const requiredFieldsFilled = Boolean(scriptUrl && caption && websiteUrl);

  useDidMountEffect(() => {
    if (!isLazyloaded || !scriptUrl || !requiredFieldsFilled) {
      return;
    }

    const script = document.createElement("script");
    script.src = scriptUrl;
    ref.current?.insertAdjacentElement("afterbegin", script);
  }, [isLazyloaded, scriptUrl, requiredFieldsFilled]);

  return (
    <ModuleWithHeadings
      className={styles.PeerTvModule}
      id={moduleId}
      colors={{ background: scheme.main.background, color: scheme.main.text }}
      title={title}
      subtitle={subtitle}
    >
      <LazyloadWrapper onLoad={setIsLazyloaded}>
        <div className={moduleBaseStyles.Module__Wrapper}>
          <ModuleHeadings
            scheme={scheme}
            isFirstOnPage={isFirstOnPage}
            textAlign={textAlign}
            title={title}
            subtitle={subtitle}
          />
        </div>
        <div
          className={ClassNames(
            styles.PeerTvModule__Content,
            moduleBaseStyles.Module__Wrapper,
            {
              [styles["PeerTvModule__Content--small"]]: size === "small",
              [styles["PeerTvModule__Content--full-width"]]:
                size === "fullWidth",
            },
          )}
          ref={ref}
        >
          {requiredFieldsFilled ? (
            <p>
              <a
                className={css({ color: scheme.main.title })}
                href={websiteUrl}
                rel="noopener"
                target="_blank"
              >
                {caption}
              </a>{" "}
              {i18n.on}{" "}
              <a
                className={css({ color: scheme.main.title })}
                href={"https://www.peer.tv/" + supportedLanguage}
                rel="noopener"
                target="_blank"
              >
                Peer.tv
              </a>
            </p>
          ) : (
            <Icon glyph="peer-tv" className={styles.PeerTvModule__Icon} />
          )}
        </div>
      </LazyloadWrapper>
    </ModuleWithHeadings>
  );
};

export default PeerTvModule;
